import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField as MuiTextField,
  Typography,
} from "@material-ui/core"
import { Field, Form, Formik } from "formik"
import { Switch, TextField } from "formik-material-ui"
import { Autocomplete } from "formik-material-ui-lab"
import { useContext } from "react"
import { useMutation, useQueryClient } from "react-query"
import * as Yup from "yup"
import { updateLabInstance } from "~/actions/labs"
import CounterHeader from "~/components/Common/CounterHeader"
import LoaderButton from "~/components/LoaderButton/index.jsx"
import AppContext from "~/contexts/App"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"

import Box from "~/components/Common/Box"
import useStyles from "./styles"

const EditInstanceDialog = () => {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const { dialogs, closeDialog, openSnackBar } = useContext(AppContext)
  const thisDialog = dialogs?.["editInstanceDialog"] || {}
  const { open = false, data } = thisDialog
  const openErrorSnackbar = useOpenErrorSnackbar()
  const updateInstanceMutation = useMutation((data) =>
    updateLabInstance(data, lab.id, instance.id),
  )

  const lab = data?.lab
  const instance = data?.instance
  const instanceTypes = data?.instanceTypes
  const locked = lab?.locked

  const handleClose = () => {
    closeDialog("editInstanceDialog")
  }

  const onSubmit = (values, actions) => {
    updateInstanceMutation.mutate(values, {
      onSuccess: (res) => {
        queryClient.setQueryData(["lab", lab.id], (oldData) => {
          return res
        })
        openSnackBar({ message: "Lab Instance Updated" })
        handleClose()
      },
      onError: (err) => {
        openErrorSnackbar(err, "Error Updating Instance")
      },
      onSettled: () => {
        actions.setSubmitting(false)
      },
    })
  }

  const schema = Yup.object().shape({
    name: Yup.string()
      .required(`Please enter a name`)
      .max(50, "Name must be less than 50 characters"),
  })

  return (
    <Dialog open={open}>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        {locked ? (
          <>
            <Typography variant="h2" className={classes.title}>
              View Instance
            </Typography>
            <Typography color="primary" variant="subtitle2">
              {instance?.name}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h2" className={classes.title}>
              Edit Instance
            </Typography>
            <Typography color="primary" variant="subtitle2">
              Update {instance?.name}
            </Typography>
          </>
        )}
      </DialogTitle>
      <Formik
        initialValues={{
          name: instance?.name || "",
          description: instance?.description || "",
          instance_type: instance?.instance_type || "",
          ami_id: instance?.ami_id || "",
          instance_security_group: instance?.instance_security_group || "",
          instance_key_name: instance?.instance_key_name || "",
          instance_username: instance?.instance_username || "",
          use_ssh_creds: Boolean(instance?.use_ssh_creds),
          run_cloud_init: Boolean(instance?.run_cloud_init),
        }}
        onSubmit={onSubmit}
        validationSchema={schema}
      >
        {({ values, errors, touched, isSubmitting }) => (
          <Form>
            <DialogContent>
              <Box>
                <CounterHeader title="Name" value={values.name} max={50} />
                <Field
                  disabled={locked}
                  name="name"
                  component={TextField}
                  variant="filled"
                  fullWidth
                />
              </Box>
              <Box marginTop="8px">
                <CounterHeader
                  title="Description"
                  value={values.description}
                  max={1000}
                />
                <Field
                  name="description"
                  disabled={locked}
                  component={TextField}
                  variant="filled"
                  fullWidth
                  multiline
                  rows={5}
                />
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-end"
              >
                <Box width="48%">
                  <Typography color="secondary" className={classes.inputLabel}>
                    Server Type
                  </Typography>
                  <Field
                    name="instance_type"
                    options={instanceTypes}
                    component={Autocomplete}
                    disabled={locked}
                    disableClearable
                    placeholder="Select"
                    renderInput={(params) => (
                      <MuiTextField
                        {...params}
                        name="instance_type"
                        error={
                          touched.instance_type && Boolean(errors.instance_type)
                        }
                        helperText={<span>{errors.instance_type}</span>}
                        variant="filled"
                      />
                    )}
                    classes={{
                      inputRoot: classes.autocompleteRoot,
                    }}
                  />
                </Box>
                <Box width="48%">
                  <Typography color="secondary" className={classes.inputLabel}>
                    AMI ID
                  </Typography>
                  <Field
                    name="ami_id"
                    disabled={locked}
                    component={TextField}
                    variant="filled"
                    fullWidth
                  />
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Box width="48%">
                  <Typography color="secondary" className={classes.inputLabel}>
                    Security Group
                  </Typography>
                  <Field
                    name="instance_security_group"
                    disabled={locked}
                    component={TextField}
                    variant="filled"
                    fullWidth
                  />
                </Box>
                <Box width="48%">
                  <Typography color="secondary" className={classes.inputLabel}>
                    Key Name
                  </Typography>
                  <Field
                    name="instance_key_name"
                    disabled={locked}
                    component={TextField}
                    variant="filled"
                    fullWidth
                  />
                </Box>
              </Box>
              <Box display="flex">
                <Box width="48%">
                  <Typography color="secondary" className={classes.inputLabel}>
                    Username
                  </Typography>
                  <Field
                    name="instance_username"
                    disabled={locked}
                    component={TextField}
                    variant="filled"
                    fullWidth
                  />
                </Box>
                <Box marginLeft="24px">
                  <Typography color="secondary" className={classes.inputLabel}>
                    Use SSH Credentials
                  </Typography>
                  <Field
                    name="use_ssh_creds"
                    component={Switch}
                    type="checkbox"
                    disabled={locked}
                  />
                </Box>
              </Box>

              <Box>
                <Typography color="secondary" className={classes.inputLabel}>
                  Run Cloud Init
                </Typography>
                <Field
                  name="run_cloud_init"
                  component={Switch}
                  type="checkbox"
                  disabled={locked}
                />
              </Box>
            </DialogContent>

            <DialogActions>
              {locked ? (
                <>
                  <Button onClick={handleClose} style={{ height: "40px" }}>
                    Close
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={handleClose} style={{ height: "40px" }}>
                    Cancel
                  </Button>
                  <LoaderButton
                    type="submit"
                    classoverrides={{
                      wrapper: classes.button,
                      button: {
                        root: classes.buttonRoot,
                      },
                    }}
                    variant="contained"
                    color="secondary"
                    working={isSubmitting}
                    size="small"
                  >
                    Save
                  </LoaderButton>
                </>
              )}
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default EditInstanceDialog
